import React, { useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Container,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { apiErrorMessage, authService } from '../../services/api';
import { Reveal } from '../../motion';

/** Must agree with MIN_PASSWORD_LENGTH on the API. */
const MIN_PASSWORD_LENGTH = 8;

/**
 * Choose a new password, using the token from the emailed link.
 *
 * The link is single-use: completing a reset changes the password hash the
 * token was issued against, which invalidates it and any other outstanding
 * link for the account.
 */
const ResetPasswordPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';

  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const problem = useMemo(() => {
    if (password && password.length < MIN_PASSWORD_LENGTH) {
      return `Use at least ${MIN_PASSWORD_LENGTH} characters.`;
    }
    if (confirmation && password !== confirmation) {
      return 'The two passwords do not match.';
    }
    return null;
  }, [password, confirmation]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (problem) return;

    setError(null);
    setSubmitting(true);

    try {
      await authService.resetPassword(token, password);
      setDone(true);
      // Long enough to read the confirmation, short enough not to strand anyone.
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(
        apiErrorMessage(err, 'Could not reset your password. Request a new link.')
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', py: 8 }}>
      <Container maxWidth="sm">
        <Reveal>
          <Paper elevation={0} sx={{ p: { xs: 3, sm: 5 }, borderRadius: 3 }}>
            {!token ? (
              <Stack spacing={2.5}>
                <Typography variant="h5" fontWeight={700}>
                  This link is incomplete
                </Typography>
                <Typography color="text.secondary">
                  Open the link straight from the email, or request a new one.
                </Typography>
                <Button component={RouterLink} to="/forgot-password" variant="contained">
                  Request a new link
                </Button>
              </Stack>
            ) : done ? (
              <Stack spacing={2.5}>
                <Typography variant="h5" fontWeight={700}>
                  Password updated
                </Typography>
                <Typography color="text.secondary">
                  You can sign in with your new password now. Taking you there…
                </Typography>
                <Button component={RouterLink} to="/login" variant="contained">
                  Go to sign in
                </Button>
              </Stack>
            ) : (
              <Box component="form" onSubmit={handleSubmit} noValidate>
                <Typography variant="h5" fontWeight={700} gutterBottom>
                  Choose a new password
                </Typography>
                <Typography color="text.secondary" sx={{ mb: 3 }}>
                  At least {MIN_PASSWORD_LENGTH} characters. This link works once.
                </Typography>

                {error && (
                  <Alert severity="error" sx={{ mb: 2.5 }}>
                    {error}
                  </Alert>
                )}

                <Stack spacing={2.5}>
                  <TextField
                    fullWidth
                    required
                    autoFocus
                    type="password"
                    label="New password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                  />
                  <TextField
                    fullWidth
                    required
                    type="password"
                    label="Confirm new password"
                    autoComplete="new-password"
                    value={confirmation}
                    onChange={(event) => setConfirmation(event.target.value)}
                    error={Boolean(problem && confirmation)}
                    helperText={confirmation ? problem : ''}
                  />
                </Stack>

                <Button
                  type="submit"
                  fullWidth
                  size="large"
                  variant="contained"
                  sx={{ mt: 3, mb: 2 }}
                  disabled={submitting || !password || !confirmation || Boolean(problem)}
                >
                  {submitting ? 'Updating…' : 'Update password'}
                </Button>

                <Typography variant="body2" color="text.secondary" textAlign="center">
                  <Link component={RouterLink} to="/login" fontWeight={600}>
                    Back to sign in
                  </Link>
                </Typography>
              </Box>
            )}
          </Paper>
        </Reveal>
      </Container>
    </Box>
  );
};

export default ResetPasswordPage;
